/**
 * StepVesselIdentity - Wizard Step 1: Basic vessel information
 * GPT Blueprint: Transform BasicInfoTab content with wizard styling
 */

import React from 'react';
import { Ship, FileText, Globe } from 'lucide-react';
import WizardSection from '../WizardSection';
import { getAvailableVesselTypes, getVesselTypeLabel } from '../../../../../config/vesselTypes';
import EndorsementsSection from './components/EndorsementsSection';
import CertificateNotices from './components/CertificateNotices';

const StepVesselIdentity = ({ draftConfig, onUpdate }) => {
  // Parse current certificate configuration
  const getCurrentCertConfig = () => {
    const current = draftConfig.certificateType || draftConfig.vesselType || '';
    
    // Base certificates (no endorsements)
    const baseCertificates = ['workboat', 'light_duty_workboat', 'pilot_boat_dedicated', 'police_boat', 'non_self_propelled'];
    
    if (baseCertificates.includes(current)) {
      return { baseCertificate: current, endorsements: [] };
    }
    
    // Parse combined certificates back to base + endorsements
    const certConfig = {
      baseCertificate: 'workboat', // Default
      endorsements: []
    };
    
    if (current.includes('towing')) certConfig.endorsements.push('towing');
    if (current.includes('pilot')) certConfig.endorsements.push('pilot_boat');
    if (current.includes('mgo_transfer')) certConfig.endorsements.push('mgo_transfer');
    if (current.includes('high_speed')) certConfig.endorsements.push('high_speed');
    // NOTE: Tender endorsements removed - tenders operate under parent vessel SMS
    
    // Determine base certificate
    if (current.includes('light_duty')) certConfig.baseCertificate = 'light_duty_workboat';
    else if (current.includes('pilot_boat_dedicated')) certConfig.baseCertificate = 'pilot_boat_dedicated';
    else if (current.includes('police_boat')) certConfig.baseCertificate = 'police_boat';
    else if (current.includes('non_self_propelled')) certConfig.baseCertificate = 'non_self_propelled';
    
    return certConfig;
  };

  const currentConfig = getCurrentCertConfig();

  const updateCertificateType = (baseCertificate, endorsements) => {
    let certificateType = baseCertificate;
    
    // Generate combined certificate type
    if (endorsements.length === 0) {
      certificateType = baseCertificate;
    } else if (endorsements.length === 1) {
      // Single endorsement
      const endorsement = endorsements[0];
      if (endorsement === 'towing') certificateType = 'workboat_towing_endorsed';
      else if (endorsement === 'pilot_boat') certificateType = 'workboat_pilot_endorsed';
      else if (endorsement === 'mgo_transfer') certificateType = 'workboat_mgo_transfer_endorsed';
      else if (endorsement === 'high_speed') certificateType = 'workboat_high_speed_endorsed';
      // NOTE: Tender certificate types removed - tenders operate under parent vessel SMS
    } else {
      // Multiple endorsements - all combinations are valid per RA analysis
      const sortedEndorsements = [...endorsements].sort();
      
      // Create descriptive certificate type for multiple endorsements
      if (endorsements.length === 2) {
        // Common 2-endorsement combinations get specific names
        if (sortedEndorsements.includes('towing') && sortedEndorsements.includes('pilot_boat')) {
          certificateType = 'workboat_towing_pilot';
        } else if (sortedEndorsements.includes('towing') && sortedEndorsements.includes('mgo_transfer')) {
          certificateType = 'workboat_towing_mgo_transfer';
        } else if (sortedEndorsements.includes('pilot_boat') && sortedEndorsements.includes('high_speed')) {
          certificateType = 'workboat_pilot_high_speed';
        } else {
          // All other 2-endorsement combinations - create from sorted names
          certificateType = `workboat_${sortedEndorsements.join('_')}`;
        }
      } else {
        // 3+ endorsements - use descriptive multiple format
        certificateType = `workboat_multiple_${endorsements.length}`;
      }
    }
    
    // DEBUG: Log what's being saved

    // Update both certificateType and vesselType for backward compatibility
    onUpdate({ 
      certificateType: certificateType,
      vesselType: certificateType,
      baseCertificate: baseCertificate,
      selectedEndorsements: endorsements
    });
  };

  const handleBaseCertificateChange = (baseCertificate) => {
    // DEBUG: Log base certificate change

    // Validate endorsements are compatible with new base certificate per RA analysis
    let validEndorsements = [...currentConfig.endorsements];
    
    // Light duty workboats cannot have TOWING endorsement (WBC3 3.9.3.5) - all others allowed
    if (baseCertificate === 'light_duty_workboat') {
      validEndorsements = validEndorsements.filter(e => e !== 'towing');
    }
    
    // Dedicated pilot boats cannot have additional endorsements
    if (baseCertificate === 'pilot_boat_dedicated') {
      validEndorsements = [];
    }
    
    // Police boats cannot have commercial endorsements
    if (baseCertificate === 'police_boat') {
      validEndorsements = [];
    }
    
    // Non-self-propelled cannot have ANY endorsements (unmanned vessel category)
    if (baseCertificate === 'non_self_propelled') {
      validEndorsements = [];
    }
    
    updateCertificateType(baseCertificate, validEndorsements);
  };

  const handleEndorsementChange = (endorsement, checked) => {
    let newEndorsements = [...currentConfig.endorsements];
    
    if (checked) {
      // Validate RA-identified regulatory incompatibilities
      if (currentConfig.baseCertificate === 'light_duty_workboat' && endorsement === 'towing') {
        alert('Light Duty Workboat Certificates cannot include towing operations per WBC3 Section 3.9.3.5');
        return;
      }
      
      if (!newEndorsements.includes(endorsement)) {
        newEndorsements.push(endorsement);
      }
    } else {
      newEndorsements = newEndorsements.filter(e => e !== endorsement);
    }
    
    updateCertificateType(currentConfig.baseCertificate, newEndorsements);
  };

  const handleChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  return (
    <WizardSection>
      {/* Step Introduction */}
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-12 md:w-12 rounded-full bg-blue-100 mb-3">
          <Ship className="h-7 w-7 md:h-6 md:w-6 text-blue-600" />
        </div>
        <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-2">
          Vessel Identity
        </h2>
        <WizardSection.Text className="max-w-2xl mx-auto">
          Let's start with basic vessel information. This establishes your vessel's identity and core characteristics.
        </WizardSection.Text>
      </div>

      {/* Regulatory Context */}
      <WizardSection.Callout variant="regulatory" title="WBC3 Context">
        Vessel identification is required for SMS compliance and regulatory documentation. 
        This information forms the foundation of your safety management system.
      </WizardSection.Callout>

      {/* Basic Identification */}
      <WizardSection.Card>
        <WizardSection.Header>Basic Identification</WizardSection.Header>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardSection.Field
            label="Vessel Name *"
            help="The name by which your vessel is known"
          >
            <WizardSection.Input
              type="text"
              value={draftConfig.vesselName || draftConfig.name || ''}
              onChange={(e) => handleChange('vesselName', e.target.value)}
              placeholder="e.g., MV Example"
            />
          </WizardSection.Field>
        </div>
      </WizardSection.Card>

      {/* WBC3 Certificate Selection */}
      <WizardSection.Card>
        <WizardSection.Header>WBC3 Certificate Selection</WizardSection.Header>
        <WizardSection.Text>
          Select your base certificate type and any applicable endorsements. This determines your vessel's 
          legal operational authorities and compliance requirements.
        </WizardSection.Text>

        <EndorsementsSection 
          currentConfig={currentConfig}
          onEndorsementChange={handleEndorsementChange}
          onBaseCertificateChange={handleBaseCertificateChange}
        />

        <CertificateNotices currentConfig={currentConfig} />
      </WizardSection.Card>

      {/* Optional Vessel Description */}
      <WizardSection.Card>
        <WizardSection.Header>Vessel Description</WizardSection.Header>
        
        <WizardSection.Field
          label="Vessel Description (Optional)"
          help="Describe your vessel's primary operations or specialized purpose"
        >
          <WizardSection.Input
            type="text"
            value={draftConfig.vesselDescription || ''}
            onChange={(e) => handleChange('vesselDescription', e.target.value)}
            placeholder="e.g., Survey vessel, offshore support, crew transfer, fish farm service"
          />
        </WizardSection.Field>
      </WizardSection.Card>

      {/* Registration Details */}
      <WizardSection.Card>
        <WizardSection.Header>Registration Details</WizardSection.Header>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardSection.Field
            label="SSR Number"
            help="Small Ship Register number (if registered)"
          >
            <WizardSection.Input
              type="text"
              value={draftConfig.ssrNumber || ''}
              onChange={(e) => handleChange('ssrNumber', e.target.value)}
              placeholder="e.g., SSR123456"
            />
          </WizardSection.Field>

          <WizardSection.Field
            label="Home Port"
            help="Base port or port of registration"
          >
            <WizardSection.Input
              type="text"
              value={draftConfig.homePort || ''}
              onChange={(e) => handleChange('homePort', e.target.value)}
              placeholder="e.g., Southampton"
            />
          </WizardSection.Field>
        </div>
      </WizardSection.Card>

      {/* Physical Characteristics */}
      <WizardSection.Card>
        <WizardSection.Header>Physical Characteristics</WizardSection.Header>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <WizardSection.Field
            label="Length Overall (m) *"
            help="Total length of the vessel in meters"
          >
            <WizardSection.Input
              type="number"
              value={draftConfig.lengthOverall || ''}
              onChange={(e) => handleChange('lengthOverall', parseFloat(e.target.value) || '')}
              placeholder="e.g., 12.5"
              min="0"
              step="0.1"
            />
          </WizardSection.Field>

          <WizardSection.Field
            label="Gross Tonnage"
            help="GT as per tonnage certificate"
          >
            <WizardSection.Input
              type="number"
              value={draftConfig.grossTonnage || ''}
              onChange={(e) => handleChange('grossTonnage', parseFloat(e.target.value) || '')}
              placeholder="e.g., 15"
              min="0"
              step="0.1"
            />
          </WizardSection.Field>

          <WizardSection.Field
            label="Draft (m)"
            help="Maximum operating draft"
          >
            <WizardSection.Input
              type="number"
              value={draftConfig.draft || ''}
              onChange={(e) => handleChange('draft', parseFloat(e.target.value) || '')}
              placeholder="e.g., 1.2"
              min="0"
              step="0.1"
            />
          </WizardSection.Field>
        </div>
      </WizardSection.Card>

      {/* Capacity Information */}
      <WizardSection.Card>
        <WizardSection.Header>Capacity Information</WizardSection.Header>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardSection.Field
            label="Maximum Persons Onboard *"
            help="Total persons including crew and passengers"
          >
            <WizardSection.Input
              type="number"
              value={draftConfig.maxPersons || ''}
              onChange={(e) => handleChange('maxPersons', parseInt(e.target.value) || '')}
              placeholder="e.g., 12"
              min="1"
              step="1"
            />
          </WizardSection.Field>

          <WizardSection.Field
            label="Passenger Capacity"
            help="Non-crew passengers only"
          >
            <WizardSection.Input
              type="number"
              value={draftConfig.passengerCapacity || ''}
              onChange={(e) => handleChange('passengerCapacity', parseInt(e.target.value) || '')}
              placeholder="e.g., 8"
              min="0"
              step="1"
            />
          </WizardSection.Field>
        </div>
      </WizardSection.Card>

      {/* Help Information */}
      <WizardSection.Callout variant="info" title="What's Next?">
        After completing vessel identity, we'll classify your vessel according to UK Workboat Code requirements. 
        This determines which regulations apply to your operation.
      </WizardSection.Callout>
    </WizardSection>
  );
};

export default StepVesselIdentity;